import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { pickValue } from './restaurantApi';

function authHeaders() {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function getLocalDateString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const WEEKDAY_NAMES = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
];

/** Weekday enum for template matching, e.g. MONDAY from YYYY-MM-DD. */
export function weekdayNameFromDateString(dateStr) {
  const text = String(dateStr ?? '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return WEEKDAY_NAMES[new Date().getDay()] ?? 'MONDAY';
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return WEEKDAY_NAMES[date.getDay()] ?? 'MONDAY';
}

export function normalizeTemplateDayOfWeek(day) {
  return String(day ?? '').trim().toUpperCase();
}

/** Default template for a calendar date (matches template day_of_week). */
export function pickDefaultMenuTemplateForDate(templates, dateStr) {
  const list = Array.isArray(templates) ? templates : [];
  const weekday = weekdayNameFromDateString(dateStr);
  const forDay = list.filter(
    (t) => normalizeTemplateDayOfWeek(t.dayOfWeek) === weekday
  );
  const pool = forDay.length > 0 ? forDay : list;
  return pool.find((t) => normalizeIsDefault(t.isDefault)) ?? pool[0] ?? null;
}

/** Build display URL from imagePath when API returns path only. */
export function buildDailyMenuImageUrl(imagePath) {
  if (!imagePath || typeof imagePath !== 'string') return null;
  const path = String(imagePath).trim().replace(/%22|"$/g, '').replace(/^\//, '');
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path.replace(/%22|"$/g, '');
  }
  const base =
    axiosRestaurantInstance.defaults.baseURL ||
    process.env.REACT_APP_API_COMPANY_URL ||
    '';
  if (!base) return null;
  try {
    return `${new URL(base).origin}/uploads/${path}`;
  } catch {
    return `${base.replace(/\/$/, '')}/uploads/${path}`;
  }
}

const DAILY_MENU_RESPONSE_SKIP_KEYS = new Set([
  'payments',
  'Payments',
  'items',
  'Items',
]);

/** Primary key for restaurant_daily_menu_t — never use food_item_id here. */
export function resolveDailyMenuRowId(itemOrId) {
  if (itemOrId == null) return null;
  if (typeof itemOrId === 'object') {
    const raw =
      itemOrId.dailyItemId ??
      itemOrId.Daily_Item_Id ??
      itemOrId.daily_item_id;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const n = Number(itemOrId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function dailyMenuRowIdsEqual(a, b) {
  const left = resolveDailyMenuRowId(a);
  const right = resolveDailyMenuRowId(b);
  return left != null && right != null && left === right;
}

/** Diet labels must not be treated as menu categories. */
const DIET_TYPE_RE = /^(VEG|NON[_\s-]?VEG|EGG|VEGAN|NONVEG)$/i;

export function isDietFoodTypeLabel(value) {
  if (value == null) return false;
  const normalized = String(value).trim().replace(/\s+/g, '_');
  return Boolean(normalized) && DIET_TYPE_RE.test(normalized);
}

/**
 * Resolve display menu category (Breakfast, Burgers, …).
 * Never returns diet type (VEG/NON-VEG) or blank.
 */
export function resolveDailyMenuCategoryName(item, categoryKey = '') {
  const candidates = [
    item?.foodCategory,
    item?.Food_Category,
    item?.food_category,
    item?.FoodCategory,
    item?.foodCategoryName,
    item?.FoodCategoryName,
    item?.food_category_name,
    item?.category,
    item?.Category,
    categoryKey,
  ];
  for (const raw of candidates) {
    const name = raw == null ? '' : String(raw).trim();
    if (!name || isDietFoodTypeLabel(name)) continue;
    return name;
  }
  return 'Uncategorized';
}

/**
 * Normalize one daily menu row (GET daily-menu-items by date).
 * foodCategory = menu category (Breakfast, Burgers); foodType = VEG | NON-VEG.
 */
export function normalizeDailyMenuItem(item, categoryKey = '') {
  if (!item || typeof item !== 'object') return item;

  const rawImagePath = item.imagePath ?? item.image_path ?? null;
  let imageUrl =
    item.imageUrl != null && String(item.imageUrl).trim()
      ? String(item.imageUrl).replace(/%22|"$/g, '').trim()
      : null;
  if (!imageUrl && item.image_url != null && String(item.image_url).trim()) {
    imageUrl = String(item.image_url).replace(/%22|"$/g, '').trim();
  }
  if (!imageUrl && rawImagePath) {
    imageUrl = buildDailyMenuImageUrl(rawImagePath);
  }

  const foodCategory = resolveDailyMenuCategoryName(item, categoryKey);
  const rawDiet =
    item.foodType ??
    item.itemFoodType ??
    item.Item_Food_Type ??
    item.food_type ??
    (isDietFoodTypeLabel(item.Food_Type) ? item.Food_Type : null);
  const itemFoodType = rawDiet == null ? '' : String(rawDiet).trim();
  const price = item.price ?? item.Price;
  const notes = item.notes ?? item.Notes ?? '';
  const description = item.description ?? item.Description ?? null;
  const cuisineOrigin = item.cuisineOrigin ?? item.CuisineOrigin ?? null;
  const dailyItemId = resolveDailyMenuRowId(item);

  return {
    ...item,
    dailyItemId,
    Daily_Item_Id: dailyItemId,
    foodItemId: item.foodItemId ?? item.Food_Item_Id ?? item.food_item_id,
    Food_Item_Id: item.foodItemId ?? item.Food_Item_Id ?? item.food_item_id,
    foodCategory,
    Food_Category: foodCategory,
    Food_Type: itemFoodType,
    itemFoodType,
    Item_Food_Type: itemFoodType,
    foodType: itemFoodType,
    foodItem: item.foodItem ?? item.Food_Item ?? item.food_item,
    Food_Item: item.foodItem ?? item.Food_Item ?? item.food_item,
    meal: item.meal ?? item.Meal ?? item.Meal_Type,
    Meal: item.meal ?? item.Meal ?? item.Meal_Type,
    Price: price,
    price,
    Quantity: item.quantity ?? item.Quantity ?? '',
    quantity: item.quantity ?? item.Quantity ?? '',
    Notes: notes,
    notes,
    description,
    Description: description,
    cuisineOrigin,
    CuisineOrigin: cuisineOrigin,
    No_of_items_available:
      item.noOfItemsAvailable ??
      item.No_of_items_available ??
      item.no_of_items_available,
    noOfItemsAvailable:
      item.noOfItemsAvailable ??
      item.No_of_items_available ??
      item.no_of_items_available,
    menuDate: item.menuDate ?? item.menu_date ?? item.Menu_Date,
    templateId: item.templateId ?? item.template_id,
    Menu_Source: normalizeTemplateSource(
      item.Menu_Source ?? item.menuSource ?? item.menu_source
    ),
    menuSource: normalizeTemplateSource(
      item.Menu_Source ?? item.menuSource ?? item.menu_source
    ),
    foodItemQuantity: item.foodItemQuantity ?? item.food_item_quantity ?? '',
    food_item_quantity: item.foodItemQuantity ?? item.food_item_quantity ?? '',
    imagePath: rawImagePath,
    imageUrl: imageUrl || null,
    agePricing: Array.isArray(item.agePricing ?? item.age_pricing)
      ? (item.agePricing ?? item.age_pricing).map((tier) => ({
          agePricingId: tier.agePricingId ?? tier.Age_Pricing_Id ?? null,
          minAge: tier.minAge ?? tier.Min_Age ?? '',
          maxAge: tier.maxAge ?? tier.Max_Age ?? '',
          quantity: tier.quantity ?? tier.Quantity ?? '',
          price: tier.price ?? tier.Price ?? '',
          status: tier.status ?? tier.Status ?? 'ACTIVE',
        }))
      : [],
  };
}

export function formatDailyMenuAvailability(value) {
  if (value === null || value === undefined || String(value).trim() === '') {
    return 'N/A';
  }
  return value;
}

/**
 * GET /restaurants/:restaurantId/daily-menu-items?menuDate=YYYY-MM-DD
 * Response: { "Breakfast": [ { dailyItemId, foodItem, foodCategory, foodType, ... } ], ... }
 */
export function parseDailyMenuByDateResponse(data) {
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data)) {
    return data.map((item) => normalizeDailyMenuItem(item));
  }

  const rows = [];
  Object.entries(data).forEach(([categoryKey, val]) => {
    if (DAILY_MENU_RESPONSE_SKIP_KEYS.has(categoryKey)) return;
    if (!Array.isArray(val)) return;
    val.forEach((item) => {
      if (item && typeof item === 'object') {
        rows.push(normalizeDailyMenuItem(item, categoryKey));
      }
    });
  });
  return rows;
}

export async function fetchDailyMenuItemsByDate(restaurantId, menuDate, options = {}) {
  const params = { menuDate };
  const menuSource = options?.menuSource ?? options?.menu_source;
  if (menuSource != null && String(menuSource).trim() !== '') {
    params.menuSource = normalizeTemplateSource(menuSource);
  }
  const salesChannel = String(
    options?.salesChannel ?? options?.sales_channel ?? ''
  )
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
  if (salesChannel === 'dinein' || salesChannel === 'dine-in' || salesChannel === 'dine') {
    params.salesChannel = 'dinein';
  } else if (salesChannel === 'online' || salesChannel === 'myeats') {
    params.salesChannel = 'online';
  }
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantDailyMenuItemsByRestaurantId(restaurantId),
    {
      params,
      headers: authHeaders(),
    }
  );
  return response.data ?? {};
}

/**
 * POST /restaurant/daily-menu/auto-publish?restaurantId=&menuDate=
 * Publishes today's (or given date's) default menu template(s) into daily menu.
 */
export async function triggerDailyMenuAutoPublish({ restaurantId, menuDate } = {}) {
  const params = {};
  if (restaurantId != null && String(restaurantId).trim() !== '') {
    params.restaurantId = Number(restaurantId);
  }
  if (menuDate != null && String(menuDate).trim() !== '') {
    params.menuDate = String(menuDate).trim();
  }
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantDailyMenuAutoPublish,
    null,
    {
      params,
      headers: authHeaders(),
    }
  );
  return response.data ?? {};
}

export function normalizeTemplateSource(val) {
  const source = String(val ?? 'MYEATS').trim().toUpperCase().replace(/-/g, '');
  if (source === 'JREATS') return 'JREATS';
  if (source === 'DINEIN') return 'DINEIN';
  return 'MYEATS';
}

export function resolveDailyMenuItemSource(item) {
  return normalizeTemplateSource(
    item?.Menu_Source ?? item?.menuSource ?? item?.menu_source
  );
}

/** Keep only daily menu rows for the given source (MYEATS | JREATS | DINEIN). */
export function filterDailyMenuItemsBySource(items, source) {
  const wanted = normalizeTemplateSource(source);
  return (Array.isArray(items) ? items : []).filter(
    (item) => resolveDailyMenuItemSource(item) === wanted
  );
}

export function formatTemplateSourceLabel(source) {
  const normalized = normalizeTemplateSource(source);
  if (normalized === 'JREATS') return 'Junior Eats';
  if (normalized === 'DINEIN') return 'Dine-In';
  return 'MyEats';
}

export function normalizeIsDefault(val) {
  if (val === true || val === 1 || val === '1' || String(val).trim().toLowerCase() === 'true') {
    return true;
  }
  return false;
}

export function formatIsDefaultLabel(val) {
  return normalizeIsDefault(val) ? 'Yes' : 'No';
}

export function formatTemplateCreatedDate(val) {
  if (val == null || String(val).trim() === '') return '—';
  const s = String(val).trim().replace(' ', 'T');
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(val);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

export function formatTemplateDetailDate(val) {
  if (val == null || String(val).trim() === '') return '—';
  const s = String(val).trim().replace(' ', 'T');
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(val);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${mins}`;
}

export function normalizeMenuTemplate(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const templateSourceRaw = pickValue(
    raw,
    'templateSource',
    'template_source',
    'Template_Source'
  );
  const isDefaultRaw = pickValue(raw, 'isDefault', 'is_default', 'Is_Default');
  return {
    templateId: pickValue(raw, 'templateId', 'template_id', 'Template_Id'),
    restaurantId: pickValue(raw, 'restaurantId', 'restaurant_id', 'Restaurant_Id'),
    templateName: pickValue(raw, 'templateName', 'template_name', 'Template_Name') ?? '',
    templateSource: normalizeTemplateSource(templateSourceRaw),
    dayOfWeek: pickValue(raw, 'dayOfWeek', 'day_of_week', 'Day_Of_Week') ?? '',
    isDefault: normalizeIsDefault(isDefaultRaw),
    status: pickValue(raw, 'status', 'Status') ?? '',
    createdDate: pickValue(raw, 'createdDate', 'created_date', 'Created_Date') ?? '',
  };
}

export function parseMenuTemplatesResponse(data) {
  if (!data || typeof data !== 'object') return [];
  const list = Array.isArray(data.templates)
    ? data.templates
    : Array.isArray(data)
      ? data
      : [];
  return list.map(normalizeMenuTemplate).filter(Boolean);
}

/** GET /restaurants/:restaurantId/menu-templates */
export async function fetchMenuTemplates(restaurantId, options = {}) {
  const params = {};
  const templateSource =
    options?.templateSource ?? options?.template_source ?? options?.menuSource;
  if (templateSource != null && String(templateSource).trim() !== '') {
    params.templateSource = normalizeTemplateSource(templateSource);
  }
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantMenuTemplatesByRestaurantId(restaurantId),
    { headers: authHeaders(), params }
  );
  return parseMenuTemplatesResponse(response.data);
}

/**
 * POST /restaurants/daily-menu-items
 * Body: { templateId, menuDate, createdById }
 */
export async function addDailyMenuFromTemplate({ templateId, menuDate, createdById }) {
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantsDailyMenuItems,
    {
      templateId: Number(templateId),
      menuDate,
      createdById: Number(createdById),
    },
    {
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    }
  );
  return response.data ?? {};
}

const DAILY_MENU_TEMPLATE_DATES_KEY = 'dailyMenuTemplateDates';
const DAILY_MENU_SKIP_AUTO_PUBLISH_KEY = 'dailyMenuSkipAutoPublish';

export function buildDailyMenuAutoPublishKey(restaurantId, menuDate, menuSource) {
  const rid = Number(restaurantId);
  const date = String(menuDate ?? '').trim();
  const source = normalizeTemplateSource(menuSource ?? 'MYEATS');
  if (!rid || Number.isNaN(rid) || !date) return '';
  return `${rid}:${date}:${source}`;
}

/** Skip frontend auto-publish when the user has managed or cleared this day's menu. */
export function markDailyMenuSkipAutoPublish(restaurantId, menuDate, menuSource) {
  const key = buildDailyMenuAutoPublishKey(restaurantId, menuDate, menuSource);
  if (!key) return;
  try {
    const stored = JSON.parse(sessionStorage.getItem(DAILY_MENU_SKIP_AUTO_PUBLISH_KEY) || '[]');
    const set = new Set(Array.isArray(stored) ? stored : []);
    set.add(key);
    sessionStorage.setItem(DAILY_MENU_SKIP_AUTO_PUBLISH_KEY, JSON.stringify([...set]));
  } catch (e) {
    console.warn('markDailyMenuSkipAutoPublish', e);
  }
}

export function shouldSkipDailyMenuAutoPublish(restaurantId, menuDate, menuSource) {
  const key = buildDailyMenuAutoPublishKey(restaurantId, menuDate, menuSource);
  if (!key) return false;
  try {
    const stored = JSON.parse(sessionStorage.getItem(DAILY_MENU_SKIP_AUTO_PUBLISH_KEY) || '[]');
    const set = new Set(Array.isArray(stored) ? stored : []);
    return set.has(key);
  } catch {
    return false;
  }
}

/** Remember menu dates where a template was applied to daily menu (for later sync). */
export function recordDailyMenuTemplateDate(templateId, menuDate) {
  const tid = templateId != null ? String(templateId).trim() : '';
  const date = menuDate != null ? String(menuDate).trim() : '';
  if (!tid || !date) return;
  try {
    const map = JSON.parse(sessionStorage.getItem(DAILY_MENU_TEMPLATE_DATES_KEY) || '{}');
    const dates = new Set(Array.isArray(map[tid]) ? map[tid] : []);
    dates.add(date);
    map[tid] = [...dates];
    sessionStorage.setItem(DAILY_MENU_TEMPLATE_DATES_KEY, JSON.stringify(map));
  } catch (e) {
    console.warn('recordDailyMenuTemplateDate', e);
  }
}

export function getDailyMenuDatesForTemplate(templateId) {
  const tid = templateId != null ? String(templateId).trim() : '';
  if (!tid) return [];
  try {
    const map = JSON.parse(sessionStorage.getItem(DAILY_MENU_TEMPLATE_DATES_KEY) || '{}');
    const dates = map[tid];
    return Array.isArray(dates) ? dates.map((d) => String(d).trim()).filter(Boolean) : [];
  } catch {
    return [];
  }
}

/**
 * Template linked to a daily menu day for a source (null if empty or mixed templates).
 * When templateSource is set, only rows for that source (MYEATS | JREATS) are considered.
 */
export function getActiveTemplateIdForDailyMenu(rows, templateSource = null) {
  let list = Array.isArray(rows) ? rows : [];
  if (templateSource != null && String(templateSource).trim() !== '') {
    list = filterDailyMenuItemsBySource(list, templateSource);
  }
  if (list.length === 0) return null;
  const ids = new Set();
  for (const item of list) {
    const tid = item.templateId ?? item.template_id;
    if (tid != null && String(tid).trim() !== '') {
      ids.add(String(tid));
    }
  }
  if (ids.size !== 1) return null;
  return Number([...ids][0]);
}

/**
 * Keep only today/future dates where daily menu already uses this template.
 */
export async function filterMenuDatesForTemplateSync(
  restaurantId,
  templateId,
  candidateDates = [],
  templateSource = null
) {
  const rid = Number(restaurantId);
  const tid = Number(templateId);
  const today = getLocalDateString();
  if (!rid || Number.isNaN(rid) || !tid || Number.isNaN(tid)) {
    return [];
  }

  const dates = [
    ...new Set(
      (Array.isArray(candidateDates) ? candidateDates : [])
        .map((d) => String(d).trim())
        .filter((d) => d && d >= today)
    ),
  ];

  const eligible = [];
  for (const menuDate of dates) {
    try {
      const normalizedSource =
        templateSource != null && String(templateSource).trim() !== ''
          ? normalizeTemplateSource(templateSource)
          : null;
      const data = await fetchDailyMenuItemsByDate(rid, menuDate, {
        menuSource: normalizedSource ?? undefined,
      });
      const rows = parseDailyMenuByDateResponse(data);
      const activeTemplateId = getActiveTemplateIdForDailyMenu(rows, normalizedSource);
      if (activeTemplateId != null && activeTemplateId === tid) {
        eligible.push(menuDate);
      }
    } catch (err) {
      console.warn(
        'filterMenuDatesForTemplateSync',
        menuDate,
        err?.response?.data || err?.message || err
      );
    }
  }
  return eligible;
}

/**
 * Re-apply template to daily menu for dates where that template is already active.
 * Does not sync if today's daily menu uses a different template (or none).
 */
export async function syncDailyMenuFromTemplate({
  templateId,
  createdById,
  restaurantId,
  menuDates,
  templateSource = null,
}) {
  const id = Number(templateId);
  const actor = Number(createdById);
  const rid = Number(
    restaurantId ?? localStorage.getItem('restaurantId') ?? 0
  );
  if (!id || Number.isNaN(id) || !actor || Number.isNaN(actor)) {
    return { results: [], totalItemsAdded: 0 };
  }

  const candidates = Array.isArray(menuDates) && menuDates.length > 0
    ? menuDates
    : getDailyMenuDatesForTemplate(id);

  const dates = await filterMenuDatesForTemplateSync(
    rid,
    id,
    candidates,
    templateSource
  );
  if (dates.length === 0) {
    return { results: [], totalItemsAdded: 0 };
  }

  const results = [];
  let totalItemsAdded = 0;

  for (const menuDate of dates) {
    try {
      const data = await addDailyMenuFromTemplate({
        templateId: id,
        menuDate,
        createdById: actor,
      });
      const added = Number(data.itemsAdded) || 0;
      totalItemsAdded += added;
      results.push({ menuDate, ok: true, data, itemsAdded: added });
    } catch (err) {
      console.warn(
        'syncDailyMenuFromTemplate',
        menuDate,
        err?.response?.data || err?.message || err
      );
      results.push({ menuDate, ok: false, error: err });
    }
  }

  return { results, totalItemsAdded };
}
